import React, {useState, useEffect} from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Avatar,
    IconButton,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    InputAdornment,
    Tooltip,
    Alert,
    Collapse,
    Tabs,
    Tab,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    Badge,
    Switch,
    FormControlLabel
} from '@mui/material';
import {styled} from '@mui/system';
import {LoadingButton} from '@mui/lab';
import {
    Plus,
    Users,
    Eye,
    EyeOff,
    Search,
    Filter,
    Key,
    Building,
    Shield,
    Mail,
    Phone,
    Calendar,
    Trash2,
    X as CloseIcon,
    Edit,
    Activity,
    Monitor,
    LogOut,
    CheckCircle,
    XCircle,
    Clock,
    Settings,
    Lock
} from 'lucide-react';
import {showSnackbar} from "../../components/SnackBar/SnackBar";
import {
    userManagementApi,
    propertyAccessApi,
    rolesApi,
    activityApi,
    sessionApi
} from "../../services/api/userManagement";
import {getLandlordId} from "../../services/api";
import {propertiesApi} from "../../services/api";

const themeColor = "#2C5283";

// ==================== STYLED COMPONENTS ====================

const StyledCard = styled(Card)(({theme}) => ({
    marginBottom: theme.spacing(2),
    border: "1px solid #DDE6ED",
    borderRadius: theme.spacing(1),
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    [theme.breakpoints.down('sm')]: {
        marginBottom: theme.spacing(1),
        borderRadius: theme.spacing(0.5),
    }
}));

const StyledDialog = styled(Dialog)(({theme}) => ({
    '& .MuiDialog-paper': {
        borderRadius: '12px',
        minWidth: '500px',
        maxHeight: '90vh',
        [theme.breakpoints.down('sm')]: {
            margin: '16px',
            width: 'calc(100% - 32px)',
            maxWidth: 'none',
            minWidth: 'unset'
        }
    },
    '& .MuiDialogTitle-root': {
        padding: '20px 24px 16px',
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: '#f8f9fa',
        [theme.breakpoints.down('sm')]: {
            padding: '16px 20px 12px',
        }
    },
    '& .MuiDialogContent-root': {
        padding: '24px',
        [theme.breakpoints.down('sm')]: {
            padding: '20px',
        }
    },
    '& .MuiDialogActions-root': {
        padding: '16px 24px 20px',
        borderTop: '1px solid #e0e0e0',
        backgroundColor: '#f8f9fa',
        [theme.breakpoints.down('sm')]: {
            padding: '12px 20px 16px',
            flexDirection: 'column',
            gap: '8px',
            '& .MuiButton-root': {
                width: '100%',
            }
        }
    },
}));

const DialogTitleStyled = styled(DialogTitle)(({theme}) => ({
    color: themeColor,
    fontWeight: 600,
    fontSize: '1.3rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
        fontSize: '1.2rem',
    }
}));

const StatsCard = styled(Card)(({theme}) => ({
    padding: theme.spacing(3),
    borderRadius: '12px',
    border: `1px solid #DDE6ED`,
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2),
    }
}));

const ActionButton = styled(IconButton)(({theme, color}) => ({
    padding: '8px',
    margin: '0 2px',
    borderRadius: '8px',
    backgroundColor: color === 'primary' ? '#e3f2fd' : color === 'error' ? '#ffebee' : color === 'warning' ? '#fff3e0' : '#f5f5f5',
    color: color === 'primary' ? '#1976d2' : color === 'error' ? '#d32f2f' : color === 'warning' ? '#f57c00' : '#666',
    '&:hover': {
        backgroundColor: color === 'primary' ? '#bbdefb' : color === 'error' ? '#ffcdd2' : color === 'warning' ? '#ffe0b2' : '#eeeeee',
    },
    transition: 'all 0.2s ease',
}));

// ==================== TAB PANEL COMPONENT ====================

function TabPanel({children, value, index, ...other}) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`user-management-tabpanel-${index}`}
            aria-labelledby={`user-management-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{py: 3}}>{children}</Box>}
        </div>
    );
}

// ==================== MAIN COMPONENT ====================

const UserManagementV2 = () => {
    // ==================== STATE MANAGEMENT ====================

    // Tab state
    const [currentTab, setCurrentTab] = useState(0);

    // Data state
    const [subUsers, setSubUsers] = useState([]);
    const [properties, setProperties] = useState([]);
    const [roles, setRoles] = useState([]);
    const [activityLogs, setActivityLogs] = useState([]);
    const [userSessions, setUserSessions] = useState([]);
    const [userPropertyAccess, setUserPropertyAccess] = useState([]); // Stores existing property access data

    // Loading states
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [updating, setUpdating] = useState(false);

    // Filter and search
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [roleFilter, setRoleFilter] = useState('all');

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showPropertyAccessModal, setShowPropertyAccessModal] = useState(false);
    const [showSessionsModal, setShowSessionsModal] = useState(false);
    const [showActivityModal, setShowActivityModal] = useState(false);

    // Selected items
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedProperties, setSelectedProperties] = useState([]);
    const [selectedRole, setSelectedRole] = useState('');
    const [propertyRoleMappings, setPropertyRoleMappings] = useState({}); // {propertyId: roleId}

    // Form state
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        password: '',
        confirmPassword: '',
        roleId: ''
    });

    // Errors
    const [errors, setErrors] = useState({});

    // Landlord ID
    const [landlordId, setLandlordId] = useState(null);

    // ==================== EFFECTS ====================

    useEffect(() => {
        const id = getLandlordId();
        setLandlordId(id);
    }, []);

    useEffect(() => {
        if (landlordId) {
            loadAllData();
        }
    }, [landlordId]);

    // ==================== DATA LOADING ====================

    const loadAllData = async () => {
        setLoading(true);
        try {
            await Promise.all([
                loadSubUsers(),
                loadProperties(),
                loadRoles(),
                loadActivityLogs()
            ]);
        } catch (error) {
            console.error("Error loading data:", error);
            showSnackbar({
                title: "Error!",
                message: "Failed to load data. Please refresh the page.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
        } finally {
            setLoading(false);
        }
    };

    const loadSubUsers = async () => {
        try {
            const response = await userManagementApi.getSubUsers(landlordId);
            if (response.ok) {
                setSubUsers(response.data.sub_users || []);
            }
        } catch (error) {
            console.error("Error loading sub-users:", error);
        }
    };

    const loadProperties = async () => {
        try {
            const response = await propertiesApi.getAll(landlordId);
            if (response.ok) {
                setProperties(response.data || []);
            }
        } catch (error) {
            console.error("Error loading properties:", error);
        }
    };

    const loadRoles = async () => {
        try {
            const response = await rolesApi.getAll();
            if (response.ok) {
                setRoles(response.data.roles || []);
            }
        } catch (error) {
            console.error("Error loading roles:", error);
        }
    };

    const loadActivityLogs = async () => {
        try {
            const response = await activityApi.getUserActivity({
                landlord_id: landlordId,
                limit: 50
            });
            if (response.ok) {
                setActivityLogs(response.data.activities || []);
            }
        } catch (error) {
            console.error("Error loading activity logs:", error);
        }
    };

    const loadUserSessions = async (userId) => {
        try {
            const response = await sessionApi.getUserSessions({
                sub_user_id: userId,
                landlord_id: landlordId
            });
            if (response.ok) {
                setUserSessions(response.data.sessions || []);
            }
        } catch (error) {
            console.error("Error loading user sessions:", error);
        }
    };

    // ==================== USER CRUD OPERATIONS ====================

    const validateForm = () => {
        const newErrors = {};

        if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email';
        }
        if (!formData.phoneNumber.trim()) {
            newErrors.phoneNumber = 'Phone number is required';
        } else if (!/^\+?[\d\s-()]+$/.test(formData.phoneNumber)) {
            newErrors.phoneNumber = 'Please enter a valid phone number';
        }
        if (!formData.roleId) {
            newErrors.roleId = 'Please select a role';
        }

        // Password validation only for create (not edit)
        if (!selectedUser) {
            if (!formData.password) {
                newErrors.password = 'Password is required';
            } else if (formData.password.length < 8) {
                newErrors.password = 'Password must be at least 8 characters';
            }
            if (formData.password !== formData.confirmPassword) {
                newErrors.confirmPassword = 'Passwords do not match';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleCreateUser = async () => {
        if (!validateForm()) return;

        setCreating(true);
        try {
            const response = await userManagementApi.createSubUser({
                landlord_id: landlordId,
                first_name: formData.firstName,
                last_name: formData.lastName,
                email_address: formData.email,
                phone_number: formData.phoneNumber,
                password: formData.password,
                role_id: formData.roleId
            });

            if (response.ok) {
                showSnackbar({
                    title: "Success!",
                    message: "User created successfully!",
                    type: "success",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                handleCloseCreateModal();
                await loadSubUsers();
                await loadActivityLogs(); // Refresh activity
            } else {
                showSnackbar({
                    title: "Error!",
                    message: response.data?.Error || "Failed to create user.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            }
        } catch (error) {
            console.error('Error creating user:', error);
            showSnackbar({
                title: "Error!",
                message: "Failed to create user. Please try again.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
        } finally {
            setCreating(false);
        }
    };

    const handleUpdateUser = async () => {
        if (!validateForm()) return;

        setUpdating(true);
        try {
            const response = await userManagementApi.updateSubUser({
                sub_user_id: selectedUser.sub_user_id,
                landlord_id: landlordId,
                first_name: formData.firstName,
                last_name: formData.lastName,
                email_address: formData.email,
                phone_number: formData.phoneNumber,
                role_id: formData.roleId
            });

            if (response.ok) {
                showSnackbar({
                    title: "Success!",
                    message: "User updated successfully!",
                    type: "success",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                handleCloseEditModal();
                await loadSubUsers();
                await loadActivityLogs();
            } else {
                showSnackbar({
                    title: "Error!",
                    message: response.data?.Error || "Failed to update user.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            }
        } catch (error) {
            console.error('Error updating user:', error);
            showSnackbar({
                title: "Error!",
                message: "Failed to update user. Please try again.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
        } finally {
            setUpdating(false);
        }
    };

    const handleToggleUserStatus = async (userId, currentStatus) => {
        try {
            const newStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
            const response = await userManagementApi.updateSubUserStatus({
                sub_user_id: userId,
                landlord_id: landlordId,
                user_status: newStatus
            });

            if (response.ok) {
                setSubUsers(subUsers.map(user =>
                    user.sub_user_id === userId
                        ? {...user, user_status: newStatus}
                        : user
                ));
                showSnackbar({
                    title: "Success!",
                    message: `User ${newStatus === 'ACTIVE' ? 'activated' : 'deactivated'} successfully!`,
                    type: "success",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                await loadActivityLogs();
            }
        } catch (error) {
            console.error('Error toggling user status:', error);
            showSnackbar({
                title: "Error!",
                message: "Failed to update user status.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
        }
    };

    const handleDeleteUser = async (userId) => {
        if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
            try {
                const response = await userManagementApi.deleteSubUser({
                    sub_user_id: userId,
                    landlord_id: landlordId
                });

                if (response.ok) {
                    setSubUsers(subUsers.filter(user => user.sub_user_id !== userId));
                    showSnackbar({
                        title: "Success!",
                        message: "User deleted successfully!",
                        type: "success",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                    await loadActivityLogs();
                }
            } catch (error) {
                console.error('Error deleting user:', error);
                showSnackbar({
                    title: "Error!",
                    message: "Failed to delete user.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            }
        }
    };

    // ==================== PROPERTY ACCESS MANAGEMENT ====================

    const handleManagePropertyAccess = async (user) => {
        setSelectedUser(user);
        setShowPropertyAccessModal(true);

        // Load current property access
        try {
            const response = await propertyAccessApi.getUserAccess({
                sub_user_id: user.sub_user_id,
                landlord_id: landlordId
            });
            if (response.ok) {
                const propertyAccessData = response.data.property_access || [];
                setUserPropertyAccess(propertyAccessData);

                // Pre-select properties and their roles
                const accessiblePropertyIds = propertyAccessData.map(p => p.property_id);
                setSelectedProperties(accessiblePropertyIds);

                // Pre-populate role mappings
                const roleMappings = {};
                propertyAccessData.forEach(access => {
                    // Find the role_id from roles array that matches role_name
                    const matchingRole = roles.find(r => r.role_name === access.role_name);
                    if (matchingRole) {
                        roleMappings[access.property_id] = matchingRole.role_id;
                    }
                });
                setPropertyRoleMappings(roleMappings);
            }
        } catch (error) {
            console.error('Error loading property access:', error);
        }
    };

    const handleUpdatePropertyAccess = async () => {
        try {
            // Validate that each selected property has a role assigned
            const invalidProperties = selectedProperties.filter(propId => !propertyRoleMappings[propId]);

            if (invalidProperties.length > 0) {
                showSnackbar({
                    title: "Error!",
                    message: "Please assign a role to each selected property.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                return;
            }

            // Grant access to each selected property with its assigned role
            const promises = selectedProperties.map(propertyId =>
                propertyAccessApi.grantAccess({
                    sub_user_id: selectedUser.sub_user_id,
                    landlord_id: landlordId,
                    property_id: propertyId,
                    role_id: propertyRoleMappings[propertyId]
                })
            );

            const responses = await Promise.all(promises);

            const allSuccessful = responses.every(response => response.ok);

            if (allSuccessful) {
                showSnackbar({
                    title: "Success!",
                    message: `Property access granted successfully for ${selectedProperties.length} ${selectedProperties.length === 1 ? 'property' : 'properties'}!`,
                    type: "success",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                handleClosePropertyAccessModal();
                await loadSubUsers();
                await loadActivityLogs();
            } else {
                showSnackbar({
                    title: "Error!",
                    message: "Some properties failed to update. Please try again.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            }
        } catch (error) {
            console.error('Error updating property access:', error);
            showSnackbar({
                title: "Error!",
                message: "Failed to update property access.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
        }
    };

    // ==================== SESSION MANAGEMENT ====================

    const handleViewSessions = async (user) => {
        setSelectedUser(user);
        setShowSessionsModal(true);
        await loadUserSessions(user.sub_user_id);
    };

    const handleRevokeSession = async (sessionId) => {
        if (window.confirm('Are you sure you want to revoke this session? The user will be logged out immediately.')) {
            try {
                const response = await sessionApi.revokeSession({
                    sub_user_id: selectedUser.sub_user_id,
                    landlord_id: landlordId,
                    session_id: sessionId
                });

                if (response.ok) {
                    showSnackbar({
                        title: "Success!",
                        message: "Session revoked successfully!",
                        type: "success",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                    await loadUserSessions(selectedUser.sub_user_id);
                    await loadActivityLogs();
                }
            } catch (error) {
                console.error('Error revoking session:', error);
                showSnackbar({
                    title: "Error!",
                    message: "Failed to revoke session.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            }
        }
    };

    // ==================== HELPER FUNCTIONS ====================

    const handleCloseCreateModal = () => {
        setShowCreateModal(false);
        setFormData({
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
            password: '',
            confirmPassword: '',
            roleId: ''
        });
        setErrors({});
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setSelectedUser(null);
        setFormData({
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
            password: '',
            confirmPassword: '',
            roleId: ''
        });
        setErrors({});
    };

    const handleClosePropertyAccessModal = () => {
        setShowPropertyAccessModal(false);
        setSelectedUser(null);
        setSelectedProperties([]);
        setPropertyRoleMappings({});
        setUserPropertyAccess([]);
    };

    const handleCloseSessionsModal = () => {
        setShowSessionsModal(false);
        setSelectedUser(null);
        setUserSessions([]);
    };

    const handleOpenEditModal = (user) => {
        setSelectedUser(user);
        setFormData({
            firstName: user.first_name,
            lastName: user.last_name,
            email: user.email_address,
            phoneNumber: user.phone_number,
            password: '',
            confirmPassword: '',
            roleId: user.role_id || ''
        });
        setShowEditModal(true);
    };

    const stringAvatar = (name) => ({
        sx: {
            bgcolor: themeColor,
            width: 40,
            height: 40,
            fontSize: '1rem'
        },
        children: name ? `${name.split(' ')[0][0]}${name.split(' ')[1] ? name.split(' ')[1][0] : ''}` : 'U',
    });

    // Filter users
    const filteredUsers = subUsers.filter(user => {
        const matchesSearch = user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email_address?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || user.user_status?.toLowerCase() === statusFilter.toLowerCase();
        const matchesRole = roleFilter === 'all' || user.role_id === parseInt(roleFilter);
        return matchesSearch && matchesStatus && matchesRole;
    });

    // Loading state
    if (loading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh'}}>
                <Box sx={{textAlign: 'center'}}>
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <Typography color="textSecondary">Loading user management...</Typography>
                </Box>
            </Box>
        );
    }

    // ==================== RENDER ====================

    return (
        <Box sx={{minHeight: '100vh'}}>
            <Box sx={{maxWidth: '1400px', margin: '0 auto'}}>
                {/* Header */}
                <Box sx={{mb: 4}}>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 2
                    }}>
                        <Box>
                            <Typography variant="h4" sx={{
                                fontWeight: 'bold',
                                color: themeColor,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 1
                            }}>
                                <Users size={32}/>
                                User Management
                            </Typography>
                            <Typography variant="body1" color="textSecondary">
                                Comprehensive RBAC system - Manage users, roles, permissions & access control
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<Plus size={20}/>}
                            onClick={() => setShowCreateModal(true)}
                            sx={{
                                background: `linear-gradient(90deg, ${themeColor}, #25446E)`,
                                '&:hover': {background: '#1E3A66'},
                                borderRadius: '10px',
                                textTransform: 'none',
                                px: 3,
                                py: 1.5,
                                fontSize: '1rem'
                            }}
                        >
                            Add New User
                        </Button>
                    </Box>
                </Box>

                {/* Stats Cards */}
                <Grid container spacing={3} sx={{mb: 4}}>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatsCard>
                            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <Box>
                                    <Typography variant="body2" color="textSecondary" sx={{fontWeight: 500}}>
                                        Total Users
                                    </Typography>
                                    <Typography variant="h4" sx={{fontWeight: 'bold', color: themeColor}}>
                                        {subUsers.length}
                                    </Typography>
                                </Box>
                                <Users size={32} color="#64B5F6"/>
                            </Box>
                        </StatsCard>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatsCard>
                            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <Box>
                                    <Typography variant="body2" color="textSecondary" sx={{fontWeight: 500}}>
                                        Active Users
                                    </Typography>
                                    <Typography variant="h4" sx={{fontWeight: 'bold', color: '#4CAF50'}}>
                                        {subUsers.filter(u => u.user_status === 'ACTIVE').length}
                                    </Typography>
                                </Box>
                                <Eye size={32} color="#81C784"/>
                            </Box>
                        </StatsCard>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatsCard>
                            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <Box>
                                    <Typography variant="body2" color="textSecondary" sx={{fontWeight: 500}}>
                                        Total Roles
                                    </Typography>
                                    <Typography variant="h4" sx={{fontWeight: 'bold', color: '#9C27B0'}}>
                                        {roles.length}
                                    </Typography>
                                </Box>
                                <Shield size={32} color="#BA68C8"/>
                            </Box>
                        </StatsCard>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatsCard>
                            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <Box>
                                    <Typography variant="body2" color="textSecondary" sx={{fontWeight: 500}}>
                                        Properties
                                    </Typography>
                                    <Typography variant="h4" sx={{fontWeight: 'bold', color: '#FF9800'}}>
                                        {properties.length}
                                    </Typography>
                                </Box>
                                <Building size={32} color="#FFB74D"/>
                            </Box>
                        </StatsCard>
                    </Grid>
                </Grid>

                {/* Tabs */}
                <StyledCard>
                    <Tabs
                        value={currentTab}
                        onChange={(e, newValue) => setCurrentTab(newValue)}
                        sx={{
                            borderBottom: 1,
                            borderColor: 'divider',
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontSize: '1rem',
                                fontWeight: 500
                            }
                        }}
                    >
                        <Tab icon={<Users size={18}/>} iconPosition="start" label="Users"/>
                        <Tab icon={<Activity size={18}/>} iconPosition="start" label="Activity Logs"/>
                        <Tab icon={<Shield size={18}/>} iconPosition="start" label="Roles & Permissions"/>
                    </Tabs>

                    {/* Tab 1: Users List */}
                    <TabPanel sx={{p: 4}} value={currentTab} index={0}>
                        {/* Filters */}
                        <Box sx={{mb: 3, p: 1}}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        placeholder="Search users by name or email..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search size={20} color="#666"/>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{'& .MuiOutlinedInput-root': {borderRadius: '10px'}}}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <FormControl fullWidth>
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            value={statusFilter}
                                            label="Status"
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                            sx={{borderRadius: '10px'}}
                                        >
                                            <MenuItem value="all">All Status</MenuItem>
                                            <MenuItem value="active">Active Only</MenuItem>
                                            <MenuItem value="inactive">Inactive Only</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <FormControl fullWidth>
                                        <InputLabel>Role</InputLabel>
                                        <Select
                                            value={roleFilter}
                                            label="Role"
                                            onChange={(e) => setRoleFilter(e.target.value)}
                                            sx={{borderRadius: '10px'}}
                                        >
                                            <MenuItem value="all">All Roles</MenuItem>
                                            {roles.map((role) => (
                                                <MenuItem key={role.role_id} value={role.role_id}>
                                                    {role.role_name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Users Table */}
                        <TableContainer component={Paper} sx={{borderRadius: '12px'}}>
                            <Table>
                                <TableHead sx={{backgroundColor: '#f8f9fa'}}>
                                    <TableRow>
                                        <TableCell sx={{fontWeight: 'bold', color: themeColor}}>User</TableCell>
                                        <TableCell sx={{fontWeight: 'bold', color: themeColor}}>Contact</TableCell>
                                        <TableCell sx={{fontWeight: 'bold', color: themeColor}}>Role</TableCell>
                                        <TableCell sx={{fontWeight: 'bold', color: themeColor}}>Status</TableCell>
                                        <TableCell sx={{fontWeight: 'bold', color: themeColor}}>Properties</TableCell>
                                        <TableCell sx={{fontWeight: 'bold', color: themeColor}}
                                                   align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredUsers.map((user) => (
                                        <TableRow key={user.sub_user_id} sx={{'&:hover': {backgroundColor: '#f8f9fa'}}}>
                                            <TableCell>
                                                <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                                                    <Avatar {...stringAvatar(`${user.first_name} ${user.last_name}`)} />
                                                    <Box>
                                                        <Typography variant="body1" sx={{fontWeight: 500}}>
                                                            {user.first_name} {user.last_name}
                                                        </Typography>
                                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                                            <Mail size={14} color="#666"/>
                                                            <Typography variant="body2" color="textSecondary">
                                                                {user.email_address}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                                    <Phone size={14} color="#666"/>
                                                    <Typography variant="body2">
                                                        {user.phone_number}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                {user.roles && user.roles.includes(',') ? (
                                                    <Tooltip title={`All roles: ${user.roles}`} arrow>
                                                        <Chip
                                                            label={user.primary_role || 'No Role'}
                                                            size="small"
                                                            sx={{backgroundColor: '#E3F2FD', color: '#1976D2', cursor: 'pointer'}}
                                                        />
                                                    </Tooltip>
                                                ) : (
                                                    <Chip
                                                        label={user.primary_role || 'No Role'}
                                                        size="small"
                                                        sx={{backgroundColor: '#E3F2FD', color: '#1976D2'}}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={user.user_status === 'ACTIVE' ? <CheckCircle size={14}/> :
                                                        <XCircle size={14}/>}
                                                    label={user.user_status}
                                                    color={user.user_status === 'ACTIVE' ? 'success' : 'error'}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                                    <Building size={16} color="#666"/>
                                                    <Typography variant="body2">
                                                        {user.assigned_properties_count || 0}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Box sx={{display: 'flex', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap'}}>
                                                    <Tooltip title="Edit User">
                                                        <ActionButton
                                                            color="primary"
                                                            onClick={() => handleOpenEditModal(user)}
                                                        >
                                                            <Edit size={16}/>
                                                        </ActionButton>
                                                    </Tooltip>
                                                    <Tooltip title="Manage Property Access">
                                                        <ActionButton
                                                            color="primary"
                                                            onClick={() => handleManagePropertyAccess(user)}
                                                        >
                                                            <Key size={16}/>
                                                        </ActionButton>
                                                    </Tooltip>
                                                    <Tooltip title="Manage Permissions">
                                                        <ActionButton
                                                            color="primary"
                                                            onClick={() => window.location.href = `/user-management/${user.sub_user_id}/permissions`}
                                                            sx={{backgroundColor: '#e8f5e9'}}
                                                        >
                                                            <Shield size={16}/>
                                                        </ActionButton>
                                                    </Tooltip>
                                                    <Tooltip title="View Sessions">
                                                        <ActionButton
                                                            onClick={() => handleViewSessions(user)}
                                                        >
                                                            <Monitor size={16}/>
                                                        </ActionButton>
                                                    </Tooltip>
                                                    <Tooltip
                                                        title={user.user_status === 'ACTIVE' ? 'Deactivate' : 'Activate'}>
                                                        <ActionButton
                                                            color="warning"
                                                            onClick={() => handleToggleUserStatus(user.sub_user_id, user.user_status)}
                                                        >
                                                            {user.user_status === 'ACTIVE' ? <EyeOff size={16}/> :
                                                                <Eye size={16}/>}
                                                        </ActionButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete User">
                                                        <ActionButton
                                                            color="error"
                                                            onClick={() => handleDeleteUser(user.sub_user_id)}
                                                        >
                                                            <Trash2 size={16}/>
                                                        </ActionButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            {filteredUsers.length === 0 && (
                                <Box sx={{textAlign: 'center', py: 8}}>
                                    <Users size={48} color="#ccc"/>
                                    <Typography variant="h6" color="textSecondary" sx={{mt: 2}}>
                                        No users found
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {searchTerm || statusFilter !== 'all' || roleFilter !== 'all'
                                            ? 'Try adjusting your search or filters'
                                            : 'Get started by creating your first sub-user'
                                        }
                                    </Typography>
                                </Box>
                            )}
                        </TableContainer>
                    </TabPanel>

                    {/* Tab 2: Activity Logs */}
                    <TabPanel sx={{p: 4}} value={currentTab} index={1}>
                        <Box sx={{mb: 2}}>
                            <Typography variant="h6" sx={{fontWeight: 600, color: themeColor, mb: 1}}>
                                Recent Activity
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Track all user actions and system events for audit compliance
                            </Typography>
                        </Box>

                        <List sx={{backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e0e0e0'}}>
                            {activityLogs.length === 0 ? (
                                <Box sx={{textAlign: 'center', py: 8}}>
                                    <Activity size={48} color="#ccc"/>
                                    <Typography variant="h6" color="textSecondary" sx={{mt: 2}}>
                                        No activity logs yet
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        User activities will appear here
                                    </Typography>
                                </Box>
                            ) : (
                                activityLogs.map((activity, index) => (
                                    <React.Fragment key={activity.id || index}>
                                        <ListItem>
                                            <ListItemIcon>
                                                <Clock size={20} color={themeColor}/>
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={activity.action || 'User Activity'}
                                                secondary={
                                                    <Box>
                                                        <Typography variant="body2" component="span">
                                                            {activity.user_name || 'System'} •{' '}
                                                        </Typography>
                                                        <Typography variant="caption" component="span"
                                                                    color="textSecondary">
                                                            {activity.timestamp ? new Date(activity.timestamp).toLocaleString() : 'Just now'}
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                        {index < activityLogs.length - 1 && <Divider/>}
                                    </React.Fragment>
                                ))
                            )}
                        </List>
                    </TabPanel>

                    {/* Tab 3: Roles & Permissions */}
                    <TabPanel sx={{p: 4}} value={currentTab} index={2}>
                        <Box sx={{mb: 2}}>
                            <Typography variant="h6" sx={{fontWeight: 600, color: themeColor, mb: 1}}>
                                Available Roles
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                View and understand the permissions associated with each role
                            </Typography>
                        </Box>

                        <Grid container spacing={3}>
                            {roles.map((role) => (
                                <Grid item xs={12} md={6} key={role.role_id}>
                                    <Card sx={{border: '1px solid #e0e0e0', borderRadius: '12px'}}>
                                        <CardContent>
                                            <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 2}}>
                                                <Shield size={24} color={themeColor}/>
                                                <Box>
                                                    <Typography variant="h6" sx={{fontWeight: 600}}>
                                                        {role.role_name}
                                                    </Typography>
                                                    <Typography variant="body2" color="textSecondary">
                                                        {role.role_description}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Divider sx={{my: 2}}/>
                                            <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 1}}>
                                                Permissions:
                                            </Typography>
                                            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                                                {role.permissions && role.permissions.length > 0 ? (
                                                    role.permissions.slice(0, 5).map((perm, index) => (
                                                        <Chip
                                                            key={index}
                                                            label={perm.permission_name}
                                                            size="small"
                                                            sx={{fontSize: '0.75rem'}}
                                                        />
                                                    ))
                                                ) : (
                                                    <Typography variant="caption" color="textSecondary">
                                                        No permissions defined
                                                    </Typography>
                                                )}
                                                {role.permissions && role.permissions.length > 5 && (
                                                    <Chip
                                                        label={`+${role.permissions.length - 5} more`}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                        sx={{fontSize: '0.75rem'}}
                                                    />
                                                )}
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </TabPanel>
                </StyledCard>

                {/* Create User Modal */}
                <StyledDialog open={showCreateModal} onClose={handleCloseCreateModal} maxWidth="sm" fullWidth>
                    <DialogTitleStyled>
                        Create New User
                        <IconButton onClick={handleCloseCreateModal} size="small">
                            <CloseIcon size={20}/>
                        </IconButton>
                    </DialogTitleStyled>
                    <DialogContent>
                        <Grid container spacing={2} sx={{mt: 1}}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="First Name"
                                    value={formData.firstName}
                                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                                    error={!!errors.firstName}
                                    helperText={errors.firstName}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Last Name"
                                    value={formData.lastName}
                                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                                    error={!!errors.lastName}
                                    helperText={errors.lastName}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Email Address"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                                    error={!!errors.email}
                                    helperText={errors.email}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Phone Number"
                                    value={formData.phoneNumber}
                                    onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})}
                                    error={!!errors.phoneNumber}
                                    helperText={errors.phoneNumber}
                                    placeholder="+254700000000"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth error={!!errors.roleId}>
                                    <InputLabel>Role</InputLabel>
                                    <Select
                                        value={formData.roleId}
                                        label="Role"
                                        onChange={(e) => setFormData({...formData, roleId: e.target.value})}
                                    >
                                        {roles.map((role) => (
                                            <MenuItem key={role.role_id} value={role.role_id}>
                                                {role.role_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.roleId && (
                                        <Typography variant="caption" color="error" sx={{mt: 0.5, ml: 2}}>
                                            {errors.roleId}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Password"
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                                    error={!!errors.password}
                                    helperText={errors.password}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Confirm Password"
                                    type="password"
                                    value={formData.confirmPassword}
                                    onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                                    error={!!errors.confirmPassword}
                                    helperText={errors.confirmPassword}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={handleCloseCreateModal}
                            variant="outlined"
                            disabled={creating}
                            sx={{borderRadius: '8px', textTransform: 'none'}}
                        >
                            Cancel
                        </Button>
                        <LoadingButton
                            onClick={handleCreateUser}
                            loading={creating}
                            variant="contained"
                            sx={{
                                background: `linear-gradient(90deg, ${themeColor}, #25446E)`,
                                '&:hover': {background: '#1E3A66'},
                                borderRadius: '8px',
                                textTransform: 'none'
                            }}
                        >
                            Create User
                        </LoadingButton>
                    </DialogActions>
                </StyledDialog>

                {/* Edit User Modal */}
                <StyledDialog open={showEditModal} onClose={handleCloseEditModal} maxWidth="sm" fullWidth>
                    <DialogTitleStyled>
                        Edit User
                        <IconButton onClick={handleCloseEditModal} size="small">
                            <CloseIcon size={20}/>
                        </IconButton>
                    </DialogTitleStyled>
                    <DialogContent>
                        <Grid container spacing={2} sx={{mt: 1}}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="First Name"
                                    value={formData.firstName}
                                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                                    error={!!errors.firstName}
                                    helperText={errors.firstName}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Last Name"
                                    value={formData.lastName}
                                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                                    error={!!errors.lastName}
                                    helperText={errors.lastName}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Email Address"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                                    error={!!errors.email}
                                    helperText={errors.email}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Phone Number"
                                    value={formData.phoneNumber}
                                    onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})}
                                    error={!!errors.phoneNumber}
                                    helperText={errors.phoneNumber}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth error={!!errors.roleId}>
                                    <InputLabel>Role</InputLabel>
                                    <Select
                                        value={formData.roleId}
                                        label="Role"
                                        onChange={(e) => setFormData({...formData, roleId: e.target.value})}
                                    >
                                        {roles.map((role) => (
                                            <MenuItem key={role.role_id} value={role.role_id}>
                                                {role.role_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={handleCloseEditModal}
                            variant="outlined"
                            disabled={updating}
                            sx={{borderRadius: '8px', textTransform: 'none'}}
                        >
                            Cancel
                        </Button>
                        <LoadingButton
                            onClick={handleUpdateUser}
                            loading={updating}
                            variant="contained"
                            sx={{
                                background: `linear-gradient(90deg, ${themeColor}, #25446E)`,
                                '&:hover': {background: '#1E3A66'},
                                borderRadius: '8px',
                                textTransform: 'none'
                            }}
                        >
                            Update User
                        </LoadingButton>
                    </DialogActions>
                </StyledDialog>

                {/* Property Access Modal */}
                <StyledDialog open={showPropertyAccessModal} onClose={handleClosePropertyAccessModal} maxWidth="sm"
                              fullWidth>
                    <DialogTitleStyled>
                        Assign Properties & Roles
                        <IconButton onClick={handleClosePropertyAccessModal} size="small">
                            <CloseIcon size={20}/>
                        </IconButton>
                    </DialogTitleStyled>
                    <DialogContent>
                        {selectedUser && (
                            <Box sx={{mb: 3}}>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 2}}>
                                    <Avatar
                                        {...stringAvatar(`${selectedUser.first_name} ${selectedUser.last_name}`)}
                                        sx={{width: 56, height: 56, fontSize: '1.3rem'}}
                                    />
                                    <Box sx={{flex: 1}}>
                                        <Typography variant="h6">
                                            {selectedUser.first_name} {selectedUser.last_name}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Manage property access and role assignments
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Summary Stats */}
                                <Grid container spacing={1.5} sx={{mb: 2}}>
                                    <Grid item xs={6}>
                                        <Paper sx={{
                                            p: 1.5,
                                            textAlign: 'center',
                                            backgroundColor: '#e3f2fd',
                                            border: '1px solid #90caf9'
                                        }}>
                                            <Typography variant="h5" sx={{fontWeight: 'bold', color: '#1976d2'}}>
                                                {userPropertyAccess.length}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                Current Access
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Paper sx={{
                                            p: 1.5,
                                            textAlign: 'center',
                                            backgroundColor: selectedProperties.length > 0 ? '#e8f5e9' : '#f5f5f5',
                                            border: selectedProperties.length > 0 ? '1px solid #81c784' : '1px solid #e0e0e0'
                                        }}>
                                            <Typography variant="h5" sx={{
                                                fontWeight: 'bold',
                                                color: selectedProperties.length > 0 ? '#388e3c' : '#666'
                                            }}>
                                                {selectedProperties.length}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                Selected Now
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>

                                <Alert severity="info" sx={{mb: 2, py: 0.5}}>
                                    <Typography variant="caption">
                                        <strong>Tip:</strong> Properties with a blue background are currently assigned. Toggle the switch to add/remove access, and select a role for each property.
                                    </Typography>
                                </Alert>
                            </Box>
                        )}

                        <Box sx={{
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            maxHeight: '500px',
                            overflow: 'auto'
                        }}>
                            {properties.map((property, index) => {
                                // Find existing access for this property
                                const existingAccess = userPropertyAccess.find(
                                    access => access.property_id === property.property_id
                                );
                                const isCurrentlyAssigned = !!existingAccess;
                                const isSelected = selectedProperties.includes(property.property_id);

                                return (
                                    <Box key={property.property_id}>
                                        <Box sx={{
                                            p: 2,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 2,
                                            backgroundColor: isCurrentlyAssigned ? '#f0f7ff' : 'transparent',
                                            transition: 'background-color 0.2s ease'
                                        }}>
                                            <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                                                <Switch
                                                    checked={isSelected}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedProperties([...selectedProperties, property.property_id]);
                                                        } else {
                                                            setSelectedProperties(selectedProperties.filter(id => id !== property.property_id));
                                                            // Remove role mapping when property is deselected
                                                            const newMappings = {...propertyRoleMappings};
                                                            delete newMappings[property.property_id];
                                                            setPropertyRoleMappings(newMappings);
                                                        }
                                                    }}
                                                    color="primary"
                                                />
                                                <Building
                                                    size={20}
                                                    color={isCurrentlyAssigned ? '#1976d2' : themeColor}
                                                />
                                                <Box sx={{flex: 1}}>
                                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.5}}>
                                                        <Typography variant="body1" sx={{fontWeight: 500}}>
                                                            {property.property_name}
                                                        </Typography>
                                                        {isCurrentlyAssigned && (
                                                            <Chip
                                                                label="Currently Assigned"
                                                                size="small"
                                                                color="primary"
                                                                sx={{
                                                                    height: '20px',
                                                                    fontSize: '0.7rem',
                                                                    fontWeight: 500
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {property.location} • {property.total_property_units} units
                                                    </Typography>
                                                    {existingAccess && (
                                                        <Box sx={{mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5}}>
                                                            <Shield size={12} color="#666"/>
                                                            <Typography variant="caption" sx={{color: '#1976d2', fontWeight: 500}}>
                                                                Current Role: {existingAccess.role_name}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary" sx={{ml: 1}}>
                                                                • Granted: {new Date(existingAccess.date_granted).toLocaleDateString()}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>

                                            {/* Role Selection - Only show when property is selected */}
                                            <Collapse in={isSelected}>
                                                <Box sx={{
                                                    pl: 7,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 1
                                                }}>
                                                    <FormControl fullWidth size="small" required>
                                                        <InputLabel>
                                                            {isCurrentlyAssigned ? 'Change Role' : 'Select Role for this Property'}
                                                        </InputLabel>
                                                        <Select
                                                            value={propertyRoleMappings[property.property_id] || ''}
                                                            label={isCurrentlyAssigned ? 'Change Role' : 'Select Role for this Property'}
                                                            onChange={(e) => {
                                                                setPropertyRoleMappings({
                                                                    ...propertyRoleMappings,
                                                                    [property.property_id]: e.target.value
                                                                });
                                                            }}
                                                            sx={{
                                                                borderRadius: '8px',
                                                                '& .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: isCurrentlyAssigned ? '#1976d2' : undefined
                                                                }
                                                            }}
                                                        >
                                                            {roles.map((role) => (
                                                                <MenuItem key={role.role_id} value={role.role_id}>
                                                                    <Box sx={{py: 0.5}}>
                                                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                                                            <Typography variant="body2" sx={{fontWeight: 500}}>
                                                                                {role.role_name}
                                                                            </Typography>
                                                                            {existingAccess?.role_name === role.role_name && (
                                                                                <Chip
                                                                                    label="Current"
                                                                                    size="small"
                                                                                    color="primary"
                                                                                    sx={{height: '18px', fontSize: '0.65rem'}}
                                                                                />
                                                                            )}
                                                                        </Box>
                                                                        <Typography variant="caption" color="textSecondary">
                                                                            {role.role_description}
                                                                        </Typography>
                                                                    </Box>
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                    {isCurrentlyAssigned && existingAccess && (
                                                        <Alert severity="info" sx={{py: 0, fontSize: '0.75rem'}}>
                                                            <Typography variant="caption">
                                                                This will update the role from <strong>{existingAccess.role_name}</strong> to your new selection
                                                            </Typography>
                                                        </Alert>
                                                    )}
                                                </Box>
                                            </Collapse>
                                        </Box>
                                        {index < properties.length - 1 && <Divider/>}
                                    </Box>
                                );
                            })}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={handleClosePropertyAccessModal}
                            variant="outlined"
                            sx={{borderRadius: '8px', textTransform: 'none'}}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleUpdatePropertyAccess}
                            variant="contained"
                            disabled={selectedProperties.length === 0}
                            sx={{
                                background: `linear-gradient(90deg, ${themeColor}, #25446E)`,
                                '&:hover': {background: '#1E3A66'},
                                borderRadius: '8px',
                                textTransform: 'none'
                            }}
                        >
                            Assign {selectedProperties.length > 0 ? `${selectedProperties.length} ${selectedProperties.length === 1 ? 'Property' : 'Properties'}` : 'Properties'}
                        </Button>
                    </DialogActions>
                </StyledDialog>

                {/* Sessions Modal */}
                <StyledDialog open={showSessionsModal} onClose={handleCloseSessionsModal} maxWidth="md" fullWidth>
                    <DialogTitleStyled>
                        Active Sessions
                        <IconButton onClick={handleCloseSessionsModal} size="small">
                            <CloseIcon size={20}/>
                        </IconButton>
                    </DialogTitleStyled>
                    <DialogContent>
                        {selectedUser && (
                            <Box sx={{mb: 3}}>
                                <Typography variant="h6">
                                    {selectedUser.first_name} {selectedUser.last_name}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Manage active sessions and force logout if needed
                                </Typography>
                            </Box>
                        )}

                        {userSessions.length === 0 ? (
                            <Box sx={{textAlign: 'center', py: 8}}>
                                <Monitor size={48} color="#ccc"/>
                                <Typography variant="h6" color="textSecondary" sx={{mt: 2}}>
                                    No active sessions
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    This user is not currently logged in
                                </Typography>
                            </Box>
                        ) : (
                            <List sx={{border: '1px solid #e0e0e0', borderRadius: '8px'}}>
                                {userSessions.map((session, index) => (
                                    <React.Fragment key={session.session_id || index}>
                                        <ListItem>
                                            <ListItemIcon>
                                                <Monitor size={20} color="#4CAF50"/>
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                                        <Typography variant="body1">
                                                            {session.device || 'Web Browser'}
                                                        </Typography>
                                                        <Chip label="Active" color="success" size="small"/>
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box>
                                                        <Typography variant="body2" component="span">
                                                            IP: {session.ip_address || 'Unknown'} •{' '}
                                                        </Typography>
                                                        <Typography variant="caption" component="span">
                                                            Last
                                                            active: {session.last_activity ? new Date(session.last_activity).toLocaleString() : 'Just now'}
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                            <Tooltip title="Revoke Session (Force Logout)">
                                                <IconButton
                                                    onClick={() => handleRevokeSession(session.session_id)}
                                                    color="error"
                                                    size="small"
                                                >
                                                    <LogOut size={18}/>
                                                </IconButton>
                                            </Tooltip>
                                        </ListItem>
                                        {index < userSessions.length - 1 && <Divider/>}
                                    </React.Fragment>
                                ))}
                            </List>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={handleCloseSessionsModal}
                            variant="contained"
                            sx={{
                                background: `linear-gradient(90deg, ${themeColor}, #25446E)`,
                                '&:hover': {background: '#1E3A66'},
                                borderRadius: '8px',
                                textTransform: 'none'
                            }}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </StyledDialog>
            </Box>
        </Box>
    );
};

export default UserManagementV2;
